import styled from '@emotion/styled'
import { memo, type ReactNode } from 'react'

import { Icon, type CompoundIconName } from '../../foundations/icon/index.ts'
import { borderRadius, colorTokens, spacing } from '../../foundations/index.ts'
import { Text } from '../../foundations/text/index.ts'

import type { badgeVariants } from './badge.util.ts'

export type BadgeVariant = (typeof badgeVariants)[number]

export type BadgeProps = {
  children?: ReactNode
  icon?: CompoundIconName | undefined
  variant?: BadgeVariant | undefined
  dark?: boolean | undefined
  className?: string | undefined
  minWidth?: string | undefined
}

export const Badge = memo<BadgeProps>(function Badge(props: BadgeProps) {
  const { children, icon, variant = 'gray', dark, className, minWidth } = props

  const colors = colorsPerVariant[variant][dark ? 'dark' : 'light']

  return (
    <BadgeContainer className={className} role="status" colors={colors} minWidth={minWidth}>
      {icon !== undefined ? (
        <IconContainer>
          <Icon icon={icon} size="10" />
        </IconContainer>
      ) : null}
      {children !== undefined ? <Text variant="caption">{children}</Text> : null}
    </BadgeContainer>
  )
})

const BadgeContainer = styled.div<{ colors: Colors; minWidth?: string | undefined }>`
  display: inline-flex;
  min-width: ${({ minWidth }) => minWidth ?? '20px'};
  min-height: 20px;
  padding: ${spacing[10]} ${spacing[20]};
  justify-content: center;
  align-items: center;
  gap: ${spacing[20]};
  max-width: max-content;

  border-radius: ${borderRadius['10']};

  color: ${({ colors }) => colors.textColor};
  background-color: ${({ colors }) => colors.backgroundColor};
  border: ${({ colors }) => (colors.borderColor ? `1px solid ${colors.borderColor}` : 'none')};
`

const IconContainer = styled.div`
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  display: flex;
`

type Colors = { backgroundColor: string; textColor: string; borderColor?: string }

const colorsPerVariant: {
  [variant in BadgeVariant]: { light: Colors; dark: Colors }
} = {
  neutral: {
    light: {
      textColor: colorTokens['color-text-base-main'],
      backgroundColor: colorTokens['color-bg-base-hover'],
      borderColor: colorTokens['color-stroke-base-basic'],
    },
    dark: {
      textColor: colorTokens['color-text-base-inverse'],
      backgroundColor: colorTokens['color-bg-base-inverse'],
    },
  },
  gray: {
    light: {
      textColor: colorTokens['color-text-base-main'],
      backgroundColor: colorTokens['color-bg-neutral-inverse'],
    },
    dark: {
      textColor: colorTokens['color-text-base-inverse'],
      backgroundColor: colorTokens['color-bg-neutral'],
    },
  },
  blue: {
    light: {
      textColor: colorTokens['color-text-base-main'],
      backgroundColor: colorTokens['color-bg-info-inverse'],
    },
    dark: {
      textColor: colorTokens['color-text-base-inverse'],
      backgroundColor: colorTokens['color-bg-info-primary'],
    },
  },
  green: {
    light: {
      textColor: colorTokens['color-text-base-main'],
      backgroundColor: colorTokens['color-bg-success-inverse'],
    },
    dark: {
      textColor: colorTokens['color-text-base-inverse'],
      backgroundColor: colorTokens['color-bg-success-primary'],
    },
  },
  orange: {
    light: {
      textColor: colorTokens['color-text-base-main'],
      backgroundColor: colorTokens['color-bg-warning-inverse'],
    },
    dark: {
      textColor: colorTokens['color-text-base-inverse'],
      backgroundColor: colorTokens['color-bg-warning-primary'],
    },
  },
  red: {
    light: {
      textColor: colorTokens['color-text-base-main'],
      backgroundColor: colorTokens['color-bg-danger-inverse'],
    },
    dark: {
      textColor: colorTokens['color-text-base-inverse'],
      backgroundColor: colorTokens['color-bg-danger-primary'],
    },
  },
  periwinkle: {
    light: {
      textColor: colorTokens['color-text-decorative-main'],
      backgroundColor: colorTokens['color-bg-decorative-1'],
    },
    dark: {
      textColor: colorTokens['color-text-decorative-1'],
      backgroundColor: colorTokens['color-bg-base-inverse'],
    },
  },

  sky: {
    light: {
      textColor: colorTokens['color-text-decorative-main'],
      backgroundColor: colorTokens['color-bg-decorative-2'],
    },
    dark: {
      textColor: colorTokens['color-text-decorative-2'],
      backgroundColor: colorTokens['color-bg-base-inverse'],
    },
  },
  mindaro: {
    light: {
      textColor: colorTokens['color-text-decorative-main'],
      backgroundColor: colorTokens['color-bg-decorative-3'],
    },
    dark: {
      textColor: colorTokens['color-text-decorative-3'],
      backgroundColor: colorTokens['color-bg-base-inverse'],
    },
  },
  jasmine: {
    light: {
      textColor: colorTokens['color-text-decorative-main'],
      backgroundColor: colorTokens['color-bg-decorative-4'],
    },
    dark: {
      textColor: colorTokens['color-text-decorative-4'],
      backgroundColor: colorTokens['color-bg-base-inverse'],
    },
  },
  peach: {
    light: {
      textColor: colorTokens['color-text-decorative-main'],
      backgroundColor: colorTokens['color-bg-decorative-5'],
    },
    dark: {
      textColor: colorTokens['color-text-decorative-5'],
      backgroundColor: colorTokens['color-bg-base-inverse'],
    },
  },
}
